import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { nights, euro, NATIONALITIES, DOCUMENT_TYPES } from "@/lib/format";
import { dayKey } from "@/pages/CalendarPage";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Users } from "lucide-react";
import { RegularBadge, GuestNotesBox } from "@/components/GuestBadges";
import { Checkbox } from "@/components/ui/checkbox";

const empty = {
  room_id: "",
  guest_name: "",
  guest_email: "",
  guest_phone: "",
  codice_fiscale: "",
  nationality: "Italia",
  birth_date: "",
  birth_place: "",
  document_type: "",
  document_number: "",
  check_in: "",
  check_out: "",
  num_guests: 1,
  status: "confirmed",
  total_amount: 0,
  notes: "",
  breakfast_pref: "standard",
  breakfast_time: "",
  allergies: "",
};

const BREAKFAST_PREFS = { standard: "Normale", early: "Anticipata", bag: "Al sacco", none: "Senza colazione" };

const GUEST_FIELDS = ["guest_name", "guest_email", "guest_phone", "codice_fiscale", "nationality", "birth_date", "birth_place", "document_type", "document_number", "num_guests"];

export default function BookingDialog({ open, onOpenChange, rooms, booking, duplicateFrom, defaultRoomId, defaultDate, presetRoomIds, onSaved }) {
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [multi, setMulti] = useState(false);
  const [roomIds, setRoomIds] = useState([]);
  const [roomGuests, setRoomGuests] = useState({});
  useEffect(() => {
    if (!open) return;
    const preset = presetRoomIds && presetRoomIds.length > 1;
    setMulti(!!preset);
    setRoomIds(preset ? presetRoomIds : []);
    setRoomGuests({});
  }, [open, presetRoomIds]);
  const toggleRoom = (id) => setRoomIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));
  const defaultGuests = (id) => (rooms.find((r) => r.id === id)?.is_double ? 2 : 1);
  const guestsFor = (id) => (roomGuests[id] !== undefined && roomGuests[id] !== "" ? Number(roomGuests[id]) : defaultGuests(id));
  const nightsN = nights(form.check_in, form.check_out);
  const groupTotal = roomIds.reduce((s, id) => s + (rooms.find((r) => r.id === id)?.base_rate || 0) * Math.max(nightsN, 0), 0);
  const groupGuests = roomIds.reduce((s, id) => s + guestsFor(id), 0);
  const [guestInfo, setGuestInfo] = useState(null);
  const [discountPct, setDiscountPct] = useState(0);
  const [discountApplied, setDiscountApplied] = useState(0);
  const isEdit = !!booking;

  useEffect(() => {
    if (!open) return;
    setDiscountApplied(0);
    api.get("/settings").then((r) => setDiscountPct(Number(r.data.loyalty_discount_pct) || 0)).catch(() => setDiscountPct(0));
  }, [open]);
  const discountValue = Math.round(Number(form.total_amount || 0) * discountPct) / 100;
  const applyDiscount = () => {
    const v = discountValue;
    setForm((f) => ({ ...f, total_amount: Math.round((Number(f.total_amount || 0) - v) * 100) / 100,
      notes: `${f.notes ? f.notes + "\n" : ""}Sconto fedeltà ${discountPct}% (−${v.toFixed(2)} €) — ospite abituale` }));
    setDiscountApplied(v);
  };

  useEffect(() => {
    if (!open) { setGuestInfo(null); return; }
    const email = (form.guest_email || "").trim(), name = (form.guest_name || "").trim();
    if (!email && name.length < 3) { setGuestInfo(null); return; }
    const t = setTimeout(() => {
      api.get("/guests/lookup", { params: { email, name, birth_date: form.birth_date || "" } })
        .then((r) => setGuestInfo(r.data.stays || r.data.notes ? r.data : null))
        .catch(() => setGuestInfo(null));
    }, 400);
    return () => clearTimeout(t);
  }, [open, form.guest_email, form.guest_name, form.birth_date]);

  useEffect(() => {
    if (!open) return;
    if (booking) {
      setForm({ ...empty, ...booking });
    } else if (duplicateFrom) {
      const guest = Object.fromEntries(GUEST_FIELDS.map((k) => [k, duplicateFrom[k] ?? empty[k]]));
      setForm({ ...empty, ...guest, room_id: duplicateFrom.room_id || "", check_in: "", check_out: "", total_amount: 0, status: "confirmed" });
    } else {
      const ci = defaultDate || "";
      const co = ci ? dayKey(new Date(new Date(ci + "T00:00:00").getTime() + 86400000)) : "";
      setForm({ ...empty, room_id: defaultRoomId || "", check_in: ci, check_out: co });
    }
  }, [open, booking, duplicateFrom, defaultRoomId, defaultDate]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // auto compute total from room rate * nights
  useEffect(() => {
    const room = rooms.find((r) => r.id === form.room_id);
    if (room && form.check_in && form.check_out) {
      const n = nights(form.check_in, form.check_out);
      if (n > 0) { set("total_amount", n * room.base_rate); setDiscountApplied(0); }
    }
    // eslint-disable-next-line
  }, [form.room_id, form.check_in, form.check_out]);

  const submit = async () => {
    const isGroup = !isEdit && multi;
    if ((isGroup ? roomIds.length === 0 : !form.room_id) || !form.guest_name || !form.check_in || !form.check_out) {
      toast.error(isGroup ? "Seleziona almeno una camera, ospite e date" : "Compila camera, ospite e date");
      return;
    }
    if (nights(form.check_in, form.check_out) <= 0) {
      toast.error("La data di check-out deve essere successiva al check-in");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, num_guests: Number(form.num_guests), total_amount: Number(form.total_amount), breakfast_pref: form.breakfast_pref || "standard", breakfast_time: ["early", "bag"].includes(form.breakfast_pref) && form.breakfast_time ? form.breakfast_time : null };
      if (isGroup) {
        const room_guests = Object.fromEntries(roomIds.filter((id) => roomGuests[id] !== undefined && roomGuests[id] !== "").map((id) => [id, Number(roomGuests[id])]));
        const { data } = await api.post("/bookings/group", { ...payload, room_ids: roomIds, room_guests });
        toast.success(`Prenotazione di gruppo creata: ${data.length} camere per ${form.guest_name}`);
        onOpenChange(false);
        onSaved && onSaved();
        return;
      }
      if (isEdit) await api.put(`/bookings/${booking.id}`, payload);
      else await api.post("/bookings", payload);
      const prev = isEdit && { room_id: booking.room_id, check_in: booking.check_in, check_out: booking.check_out };
      const moved = prev && (prev.room_id !== payload.room_id || prev.check_in !== payload.check_in || prev.check_out !== payload.check_out);
      toast.success(isEdit ? "Prenotazione aggiornata" : "Prenotazione creata", moved ? {
        duration: 8000,
        action: {
          label: "Annulla",
          onClick: async () => {
            try {
              await api.patch(`/bookings/${booking.id}/dates`, prev);
              toast.success("Spostamento annullato");
              onSaved && onSaved();
            } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
          },
        },
      } : undefined);
      onOpenChange(false);
      onSaved && onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="booking-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">
            {isEdit ? "Modifica prenotazione" : duplicateFrom ? "Nuova prenotazione (copia)" : multi ? "Nuova prenotazione di gruppo" : "Nuova prenotazione"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-xs uppercase tracking-wider">{multi ? "Camere (gruppo)" : "Camera"}</Label>
              {!isEdit && (
                <label className="flex items-center gap-1.5 text-xs cursor-pointer text-muted-foreground">
                  <Checkbox data-testid="booking-multi-toggle" checked={multi} onCheckedChange={(v) => setMulti(!!v)} />Più camere con un solo nome
                </label>
              )}
            </div>
            {multi ? (
              <div className="mt-1.5 grid grid-cols-2 gap-1.5" data-testid="booking-room-checklist">
                {rooms.map((r) => (
                  <label key={r.id} className={`flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-sm cursor-pointer ${roomIds.includes(r.id) ? "border-accent bg-accent/10 col-span-2" : "border-border"}`}>
                    <Checkbox data-testid={`booking-room-check-${r.id}`} checked={roomIds.includes(r.id)} onCheckedChange={() => toggleRoom(r.id)} />
                    <span className="truncate">{r.name}</span>{r.is_double && <span className="shrink-0 whitespace-nowrap text-[10px] rounded-full bg-accent/15 text-accent px-1.5">doppia</span>}
                    <span className="ml-auto shrink-0 text-xs text-muted-foreground">€{r.base_rate}</span>
                    {roomIds.includes(r.id) && (
                      <span className="shrink-0 inline-flex items-center gap-1" onClick={(e) => e.preventDefault()}>
                        <Users className="w-3 h-3 text-muted-foreground" />
                        <Input type="number" min="1" max="20" data-testid={`booking-room-guests-${r.id}`} value={roomGuests[r.id] ?? defaultGuests(r.id)} onChange={(e) => setRoomGuests((g) => ({ ...g, [r.id]: e.target.value }))} className="h-7 w-14 px-1.5 text-center text-xs" />
                      </span>
                    )}
                  </label>
                ))}
                <p className="col-span-2 text-xs text-muted-foreground" data-testid="booking-group-total">{roomIds.length} camere · {groupGuests} ospiti (doppie = 2, altre = 1, modificabili per camera) · totale stimato {euro(groupTotal)} ({Math.max(nightsN || 0, 0)} notti)</p>
              </div>
            ) : (
            <Select value={form.room_id} onValueChange={(v) => set("room_id", v)}>
              <SelectTrigger data-testid="booking-room-select" className="mt-1.5">
                <SelectValue placeholder="Seleziona camera" />
              </SelectTrigger>
              <SelectContent>
                {rooms.map((r) => (
                  <SelectItem key={r.id} value={r.id}>
                    {r.name} — €{r.base_rate}/notte
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            )}
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider">Nome ospite</Label>
            <Input data-testid="booking-guest-name" className="mt-1.5" value={form.guest_name} onChange={(e) => set("guest_name", e.target.value)} placeholder="Mario Rossi" />
          </div>
          {guestInfo && (
            <div className="space-y-2" data-testid="booking-guest-info">
              <div className="flex items-center gap-2 flex-wrap text-xs">
                <RegularBadge stays={guestInfo.stays} testId="booking-regular-badge" />
                {guestInfo.stays > 0 && !guestInfo.regular && <span className="text-muted-foreground">Ospite già registrato ({guestInfo.stays} soggiorno)</span>}
                <Link to={`/ospiti?sel=${encodeURIComponent(guestInfo.key)}`} data-testid="booking-guest-link" className="text-accent underline underline-offset-2 ml-auto" onClick={() => onOpenChange(false)}>Scheda ospite</Link>
              </div>
              <GuestNotesBox notes={guestInfo.notes} testId="booking-guest-notes" />
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider">Email</Label>
              <Input className="mt-1.5" value={form.guest_email || ""} onChange={(e) => set("guest_email", e.target.value)} />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Telefono</Label>
              <Input className="mt-1.5" value={form.guest_phone || ""} onChange={(e) => set("guest_phone", e.target.value)} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider">Codice Fiscale</Label>
              <Input className="mt-1.5" value={form.codice_fiscale || ""} onChange={(e) => set("codice_fiscale", e.target.value)} />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Nazionalità</Label>
              <Select value={form.nationality} onValueChange={(v) => set("nationality", v)}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {NATIONALITIES.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider">Data di nascita</Label>
              <Input type="date" data-testid="booking-birth-date" className="mt-1.5" value={form.birth_date || ""} onChange={(e) => set("birth_date", e.target.value)} />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Luogo di nascita</Label>
              <Input data-testid="booking-birth-place" className="mt-1.5" value={form.birth_place || ""} onChange={(e) => set("birth_place", e.target.value)} placeholder="Napoli (NA)" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider">Tipo documento</Label>
              <Select value={form.document_type || ""} onValueChange={(v) => set("document_type", v)}>
                <SelectTrigger data-testid="booking-doc-type" className="mt-1.5"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent>
                  {DOCUMENT_TYPES.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Numero documento</Label>
              <Input data-testid="booking-doc-number" className="mt-1.5" value={form.document_number || ""} onChange={(e) => set("document_number", e.target.value)} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider">Check-in</Label>
              <Input type="date" data-testid="booking-checkin" className="mt-1.5" value={form.check_in} onChange={(e) => set("check_in", e.target.value)} />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Check-out</Label>
              <Input type="date" data-testid="booking-checkout" className="mt-1.5" value={form.check_out} onChange={(e) => set("check_out", e.target.value)} />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider">Ospiti{roomIds.length > 1 ? " (totale gruppo)" : ""}</Label>
              {roomIds.length > 1
                ? <div className="mt-1.5 h-10 flex items-center rounded-md border border-border bg-secondary/50 px-3 text-sm" data-testid="booking-group-guests">{groupGuests} totali</div>
                : <Input type="number" min="1" className="mt-1.5" value={form.num_guests} onChange={(e) => set("num_guests", e.target.value)} />}
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Stato</Label>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="booking-status-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="confirmed">Confermata</SelectItem>
                  <SelectItem value="pending">In attesa</SelectItem>
                  <SelectItem value="checkedin">Check-in</SelectItem>
                  <SelectItem value="checkedout">Check-out</SelectItem>
                  <SelectItem value="cancelled">Annullata</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Totale €</Label>
              <Input type="number" data-testid="booking-total" className="mt-1.5" value={form.total_amount} onChange={(e) => set("total_amount", e.target.value)} />
            </div>
          </div>
          {guestInfo?.regular && discountPct > 0 && !isEdit && !discountApplied && Number(form.total_amount) > 0 && (
            <div data-testid="loyalty-discount-box" className="flex items-center justify-between gap-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              <span>Ospite abituale: applica sconto <b>{discountPct}%</b> (−{euro(discountValue)})</span>
              <Button type="button" size="sm" variant="outline" data-testid="apply-loyalty-discount" onClick={applyDiscount} className="h-7 border-amber-400 text-amber-900 hover:bg-amber-100">Applica</Button>
            </div>
          )}
          {discountApplied && <p data-testid="loyalty-discount-applied" className="text-xs text-emerald-700">Sconto fedeltà {discountPct}% applicato (−{euro(discountApplied)}).</p>}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider">Colazione</Label>
              <Select value={form.breakfast_pref || "standard"} onValueChange={(v) => set("breakfast_pref", v)}>
                <SelectTrigger data-testid="booking-breakfast-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(BREAKFAST_PREFS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {["early", "bag"].includes(form.breakfast_pref) && (
              <div>
                <Label className="text-xs uppercase tracking-wider">Ora colazione</Label>
                <Input type="time" data-testid="booking-breakfast-time" className="mt-1.5" value={form.breakfast_time || ""} onChange={(e) => set("breakfast_time", e.target.value)} />
              </div>
            )}
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-red-700 dark:text-red-300">Allergie / intolleranze</Label>
            <Input data-testid="booking-allergies" className="mt-1.5 border-red-300/60 focus-visible:ring-red-400" value={form.allergies || ""} onChange={(e) => set("allergies", e.target.value)} placeholder="Es. celiachia, lattosio, frutta a guscio…" />
            <p className="text-[11px] text-muted-foreground mt-1">Compare evidenziato nella Mensa e nel PDF per la cucina.</p>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider">Note</Label>
            <Textarea className="mt-1.5" value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} rows={2} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button data-testid="booking-save-button" onClick={submit} disabled={saving} className="bg-accent hover:bg-accent/90 text-accent-foreground">
            {saving ? "Salvataggio…" : isEdit ? "Salva modifiche" : "Crea prenotazione"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
